import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, FormControlLabel, Grid, ListItem, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { ErrorOutline } from '@mui/icons-material';

import { FCheckbox, FHAutocomplete, FHRadio, FHRadioGroup, FHSelect } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import {
  FORTA_BLACK,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY2,
  FORTA_ORANGE,
} from '../../../../../utils/colors';
import { Themes, BackgroundTypes } from '../../../../../constants';
import {
  GoalTypes,
  GOAL_DETAILS_FIELD_IDS,
  TreatmentPlanResourceVersion,
} from '../common/constants/general';
import HypothesizedFunction from '../common/components/hypothesizedFunction';
import { useAutoSelectSingleFocusArea } from '../common/hooks/useAutoSelectSingleFocusArea';
import { useAutoSelectSingleGoalName } from '../common/hooks/useAutoSelectSingleGoalName';
import { getSingleFocusAreaBehaviorId, getSingleOptionId } from '../common/utils/autoSelect';
import { filterBehaviorsByGoalType } from '../common/utils/general';

import type {
  IBehavior,
  IBehaviorGoalName,
  IOption,
  ITreatmentPlanGoal,
  ITreatmentPlanSymptom,
  SelectValueType,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';
import type { ChangeEvent } from 'react';

interface GoalDetailsProps {
  goalIndex: number;
  goal: ITreatmentPlanGoal;
  selectedGoalNameIds: number[];
  treatmentPlanId: number;
  goalNames: IBehaviorGoalName[];
  errors: any;
  onUpdateField: (key: string, value: any) => void;
}

function GoalDetails(props: GoalDetailsProps) {
  const {
    goalIndex,
    goal,
    goalNames,
    errors,
    treatmentPlanId,
    selectedGoalNameIds,
    onUpdateField,
  } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const { list: symptoms } = useSelector((state: RootState) => state.maladaptiveBehavior);
  const { list: behaviors, goalNames: behaviorGoalNamesMap } = useSelector(
    (state: RootState) => state.behaviors,
  );
  const { skillAcquisitionGoals } = useSelector((state: RootState) => state.goals);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    if (!goal) return;
    if (
      !Object.prototype.hasOwnProperty.call(goal, GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID) ||
      (!goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] &&
        goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID])
    ) {
      // Run onUpdateField for maladaptive_symptom_id after completion of
      // all other updates related to is_maladaptive_correction field
      setTimeout(() => {
        onUpdateField(GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID, null);
      }, 0);
    }
  }, [goal?.[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]]);

  const singleGoalNameId = getSingleOptionId(goalNames);
  const { setUserClearedGoalName } = useAutoSelectSingleGoalName({
    singleGoalNameId,
    isEditing: Boolean(goal?.is_editing),
    currentGoalNameId: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID],
    onUpdateField,
    goalNameFieldId: GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID,
  });

  const filteredBehaviors = useMemo(() => {
    if (!behaviorGoalNamesMap) return behaviors;
    const filtered =
      behaviors?.filter((b) => {
        if (b?.id === goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id) {
          // Show focus area option, if already selected in goal
          return true;
        } else if (b?.id !== undefined && b.id in behaviorGoalNamesMap) {
          // Goal names for the selected behaviors are loaded
          if (behaviorGoalNamesMap[b.id].length === 0) return false;
          const mappedGoalIds = behaviorGoalNamesMap[b.id]
            .map((g) => g.id)
            .filter((id): id is number => id != null);
          const filteredMappedGoalIds = mappedGoalIds.filter((id) => !selectedGoalNameIds.includes(id));
          return filteredMappedGoalIds.length > 0 ? true : false;
        } else {
          // Goal names for the selected behaviors are not loaded, allow the option to be selected.
          return true;
        }
      }) ?? [];
    return filterBehaviorsByGoalType(filtered, goal?.goal_type);
  }, [
    behaviorGoalNamesMap,
    selectedGoalNameIds,
    behaviors,
    goalNames,
    goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id,
  ]);

  const handleFocusAreaUpdate = (value: SelectValueType) => {
    onUpdateField(GOAL_DETAILS_FIELD_IDS.GOAL_NAME, {
      ...goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME],
      behavior: { id: value },
    });
  };

  const singleFocusAreaId = getSingleFocusAreaBehaviorId(filteredBehaviors);
  useAutoSelectSingleFocusArea({
    singleFocusAreaId,
    isEditing: Boolean(goal?.is_editing),
    currentFocusAreaId: goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id,
    onSelectFocusArea: handleFocusAreaUpdate,
  });

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
  }, [isTpEditable, goal]);

  const activeSkillAcquisitionGoals = useMemo(() => {
    if (!skillAcquisitionGoals) return [];

    return skillAcquisitionGoals.filter((goal: ITreatmentPlanGoal) => Object.hasOwn(goal, 'id'));
  }, [skillAcquisitionGoals]);

  const symptom = useMemo(() => {
    if (!symptoms || !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]) return;

    const symptom = symptoms?.find(
      (s: ITreatmentPlanSymptom) => s.id === goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID],
    );

    return `${symptom?.label} - ${symptom?.associated_dsm5_symptom}`;
  }, [symptoms, goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]]);

  const handleMaladaptiveCorrectionUpdate = (value: string) => {
    if (value === 'Yes' && symptoms.length === 0) {
      toast.error('Treatment plan does not have a maladaptive behavior');
      onUpdateField(GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION, false);
      return;
    }
    onUpdateField(GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION, value === 'Yes' ? true : false);
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
      <Grid container spacing={3}>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Focus area
          </Typography>
          {goal?.is_editing ? (
            <FHSelect
              data-testid="focusAreaDropDown"
              inputHeight={40}
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.Colored}
              displayEmpty
              disabled={!isEditable}
              selectFirstOption={false}
              renderValue={
                !goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id
                  ? () => <Typography>Select focus area</Typography>
                  : undefined
              }
              options={[...filteredBehaviors]
                ?.sort((behaviorA, behaviorB) =>
                  behaviorA?.label?.toLowerCase().localeCompare(behaviorB?.label?.toLowerCase()),
                )
                ?.map((behavior: IBehavior) => ({
                  value: behavior.id ?? '',
                  label: capitalizeFirstLetter(behavior.label),
                }))}
              optionDataTestIdPrefix="focusAreaOption"
              value={goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id || ''}
              onUpdate={handleFocusAreaUpdate}
              error={errors && errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]}
            />
          ) : (
            <Typography align="left">
              {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.label ?? '')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Prefix
          </Typography>
          <Typography
            data-testid={capitalizeFirstLetter(
              goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.prefix ?? '',
            ).replace(/\s+/g, '_')}
            align="left"
          >
            {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.prefix ?? '')}
          </Typography>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography fontWeight={'bold'} align="left" mb={1}>
            Goal
          </Typography>
          {goal?.is_editing ? (
            <FHAutocomplete
              data-testid="goalNameDropDown"
              inputHeight={40}
              fullWidth
              colorTheme={Themes.Primary}
              coloredBackground
              placeholder="Select goal"
              disabled={!isEditable}
              isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
              options={
                goalNames
                  ?.sort((goalA, goalB) =>
                    goalA?.label?.toLowerCase().localeCompare(goalB?.label?.toLowerCase()),
                  )
                  .map((goalName: IBehaviorGoalName) => ({
                    id: goalName.id,
                    label: capitalizeFirstLetter(goalName.label),
                  })) ?? []
              }
              renderOption={(props: any, option: IOption) => {
                return (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                );
              }}
              value={
                goalNames &&
                goalNames.length > 0 &&
                goal &&
                goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]
                  ? {
                      id: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID],
                      label: goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label,
                    }
                  : null
              }
              onChange={(event: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                if (newValue == null) {
                  setUserClearedGoalName();
                }
                onUpdateField(
                  GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID,
                  newValue?.id != null ? newValue.id : null,
                );
              }}
              error={errors ? Boolean(errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]) : false}
            />
          ) : (
            <Typography align="left">
              {capitalizeFirstLetter(goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ?? '')}
            </Typography>
          )}
        </Grid>
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12}>
            <HypothesizedFunction
              goalIndex={goalIndex}
              treatmentPlanId={treatmentPlanId}
              goalId={goal.id}
              version={TreatmentPlanResourceVersion.V1}
              hypothesizedFunctions={goal.hypothesized_functions ?? []}
              isEditable={isEditable && Boolean(goal?.is_editing)}
            />
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.SkillAcquisition && (
          <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={'bold'} align="left" mb={1}>
              Linked behavior reduction goal(s)
            </Typography>
            {goal.linked_goals?.behavior_reduction &&
              goal.linked_goals?.behavior_reduction?.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${FORTA_GRAY2}`,
                    borderRadius: '10px',
                    padding: 0,
                    gap: 0,
                  }}
                >
                  {goal.linked_goals?.behavior_reduction?.map(
                    (linkedBehaviorReductionGoal, index, array) => (
                      <ListItem
                        data-testid={(
                          linkedBehaviorReductionGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ||
                          ''
                        ).replace(/\s+/g, '_')}
                        key={linkedBehaviorReductionGoal.id}
                        sx={{
                          p: 1.2,
                          borderBottom:
                            index === array.length - 1 ? 'none' : `1px solid ${FORTA_GRAY2}`,
                        }}
                      >
                        {capitalizeFirstLetter(
                          linkedBehaviorReductionGoal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label ||
                            '',
                        )}
                      </ListItem>
                    ),
                  )}
                </Box>
              )}
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              fontWeight={'bold'}
              align="left"
              mb={1}
              color={
                errors && errors[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]
                  ? FORTA_ERROR
                  : FORTA_BLACK
              }
            >
              Is a maladaptive behavior related to this goal?
            </Typography>
            {goal?.is_editing ? (
              <FHRadioGroup
                data-testid="maladaptiveRadioGroup"
                rowGap="16px"
                colorTheme={Themes.Primary}
                coloredBackground
                defaultValue={'No'}
                value={goal?.[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] ? 'Yes' : 'No'}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleMaladaptiveCorrectionUpdate(e.target.value);
                }}
              >
                <FormControlLabel
                  data-testid="maladaptiveYesChoice"
                  sx={{ width: 'fit-content' }}
                  value="Yes"
                  control={<FHRadio colorTheme={Themes.Primary} />}
                  label="Yes"
                  disabled={!isEditable}
                />
                <FormControlLabel
                  data-testid="maladaptiveNoChoice"
                  sx={{ width: 'fit-content' }}
                  value="No"
                  control={<FHRadio colorTheme={Themes.Primary} />}
                  label="No"
                  disabled={!isEditable}
                />
              </FHRadioGroup>
            ) : (
              <Typography align="left">
                {goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] ? 'Yes' : 'No'}
              </Typography>
            )}
          </Grid>
        )}
        {symptoms.length > 0 && goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography fontWeight={'bold'} align="left" mb={1}>
              If yes, please include DSM5 symptom
            </Typography>
            {goal?.is_editing ? (
              <FHSelect
                data-testid="dSM5SymptomDropDown"
                sx={{ flexGrow: 1, width: 'auto' }}
                inputHeight={40}
                colorTheme={Themes.Primary}
                backgroundType={BackgroundTypes.EvenColored}
                displayEmpty
                disabled={!isEditable}
                renderValue={
                  !goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]
                    ? () => <Typography>Select DSM5 symptom</Typography>
                    : undefined
                }
                options={symptoms.map((symptom: ITreatmentPlanSymptom) => ({
                  value: symptom.id ?? '',
                  label: `${symptom.label} - ${symptom.associated_dsm5_symptom}`,
                }))}
                optionDataTestIdPrefix="dSM5SymptomOption"
                value={goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID] || ''}
                onUpdate={(value: SelectValueType) => {
                  setTimeout(() => {
                    onUpdateField(GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID, value);
                  });
                }}
                error={errors && errors[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID]}
              />
            ) : (
              <Typography align="left">{symptom}</Typography>
            )}
          </Grid>
        )}
        {goal?.goal_type === GoalTypes.BehaviorReduction && (
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Grid container>
              <Grid item xs={6}>
                <Typography fontWeight={'bold'} align="left" mb={1}>
                  Linked skill acquisition goal (s)*
                </Typography>
                {goal?.is_editing ? (
                  activeSkillAcquisitionGoals.length > 0 ? (
                    activeSkillAcquisitionGoals.map((saGoal: ITreatmentPlanGoal) => (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          backgroundColor: FORTA_BLUE.light3,
                          p: 1,
                          borderRadius: 2,
                        }}
                        key={saGoal?.id}
                      >
                        <Box
                          key={saGoal.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            p: 1.5,
                            pl: 2,
                            borderRadius: 1,
                          }}
                        >
                          <FCheckbox
                            data-testid={(
                              saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || ''
                            ).replace(/\s+/g, '_')}
                            colorTheme={Themes.Primary}
                            coloredBackground
                            checked={goal?.linked_goals?.skill_acquisition?.some(
                              (lg) => lg?.id === saGoal.id,
                            )}
                            iconStyles={{
                              boxShadow:
                                goal?.is_editing && isEditable
                                  ? '0px 4px 4px rgba(0, 0, 0, 0.25)'
                                  : 'none',
                              width: '25px',
                              height: '25px',
                              border: `1px solid ${FORTA_GRAY2}`,
                            }}
                            sx={{
                              cursor: `${
                                goal?.is_editing && isEditable ? 'pointer' : 'not-allowed'
                              } !important`,
                            }}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const isChecked = e.target.checked;
                              const currentLinkedGoals =
                                goal?.linked_goals?.skill_acquisition || [];

                              const updatedGoals = isChecked
                                ? [
                                    ...currentLinkedGoals,
                                    {
                                      id: saGoal.id,
                                      goal_name: saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME],
                                    },
                                  ]
                                : currentLinkedGoals.filter(
                                    (linkedGoal) => linkedGoal?.id !== saGoal.id,
                                  );

                              onUpdateField(GOAL_DETAILS_FIELD_IDS.LINKED_SKILL_ACQUISITION_GOALS, {
                                ...goal.linked_goals,
                                skill_acquisition: updatedGoals,
                              });
                            }}
                          />
                          <Typography>
                            {capitalizeFirstLetter(
                              saGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '',
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorOutline sx={{ fill: FORTA_ERROR }} />
                      <Typography align="left" color={FORTA_ERROR}>
                        No linkable skill acquisition goals found
                      </Typography>
                    </Box>
                  )
                ) : goal?.linked_goals?.skill_acquisition?.length &&
                  goal?.linked_goals?.skill_acquisition?.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      backgroundColor: FORTA_BLUE.light3,
                      p: 1,
                      borderRadius: 2,
                    }}
                  >
                    {goal?.linked_goals?.skill_acquisition?.map((linkedGoal, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: 'white',
                          p: 1.5,
                          pl: 2,
                          borderRadius: 1,
                        }}
                      >
                        <FCheckbox
                          data-testid={(
                            goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || ''
                          ).replace(/\s+/g, '_')}
                          colorTheme={Themes.Primary}
                          coloredBackground
                          checked={goal?.linked_goals?.skill_acquisition?.some(
                            (lg) => lg?.id === linkedGoal.id,
                          )}
                          iconStyles={{
                            boxShadow: 'none',
                            width: '25px',
                            height: '25px',
                            border: `1px solid ${FORTA_GRAY2}`,
                          }}
                          disabled={true}
                          sx={{
                            cursor: 'not-allowed !important',
                            '& .MuiCheckbox-root': {
                              cursor: 'not-allowed !important',
                            },
                          }}
                        />
                        <Typography>
                          {capitalizeFirstLetter(
                            linkedGoal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.label || '',
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutline sx={{ fill: FORTA_ORANGE }} />
                    <Typography align="left" color={FORTA_ORANGE}>
                      No skill acquisition goals are linked to this goal
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default GoalDetails;
